/**
 * Site mode helper — chooses which routes are exposed on the current build.
 *
 * ONE bundle serves all three subdomains; the mode is detected at page load
 * from ``window.location.hostname``:
 *
 *   facilitrack.co.uk / www.facilitrack.co.uk  → mode = "marketing"
 *   app.facilitrack.co.uk                      → mode = "app"
 *   anything else (localhost, preview, IPs)    → mode = "all" (dev — every route works)
 *
 * The build-time env var ``REACT_APP_MODE`` still wins if explicitly set —
 * useful for previewing marketing/app mode outside of production DNS.
 */

const MARKETING_HOSTS = new Set(['facilitrack.co.uk', 'www.facilitrack.co.uk']);
const APP_HOSTS = new Set(['app.facilitrack.co.uk']);

function detectMode() {
  const explicit = (process.env.REACT_APP_MODE || '').toLowerCase();
  if (explicit === 'marketing' || explicit === 'app' || explicit === 'all') {
    return explicit;
  }
  if (typeof window === 'undefined') return 'all';
  const host = window.location.hostname.toLowerCase();
  if (MARKETING_HOSTS.has(host)) return 'marketing';
  if (APP_HOSTS.has(host)) return 'app';
  return 'all';
}

function detectAppUrl() {
  const explicit = (process.env.REACT_APP_APP_URL || '').replace(/\/$/, '');
  if (explicit) return explicit;
  if (typeof window !== 'undefined' && MARKETING_HOSTS.has(window.location.hostname.toLowerCase())) {
    return 'https://app.facilitrack.co.uk';
  }
  return '';
}

function detectMarketingUrl() {
  const explicit = (process.env.REACT_APP_MARKETING_URL || '').replace(/\/$/, '');
  if (explicit) return explicit;
  if (typeof window !== 'undefined' && APP_HOSTS.has(window.location.hostname.toLowerCase())) {
    return 'https://www.facilitrack.co.uk';
  }
  return '';
}

export const SITE_MODE = detectMode();
export const APP_URL = detectAppUrl();
export const MARKETING_URL = detectMarketingUrl();

export const IS_MARKETING = SITE_MODE === 'marketing';
export const IS_APP = SITE_MODE === 'app';
export const IS_ALL = SITE_MODE === 'all';

/**
 * Given an app-side path (e.g. "/login", "/dashboard"), return the correct
 * href. In marketing mode this becomes an absolute cross-domain URL so the
 * `Sign in` link lands the user on the app subdomain.
 */
export function appHref(path) {
  const p = path.startsWith('/') ? path : `/${path}`;
  if (IS_MARKETING && APP_URL) return `${APP_URL}${p}`;
  return p;
}

/**
 * Given a marketing-side path (e.g. "/features"), return the correct href.
 * In app mode this becomes an absolute cross-domain URL back to the
 * marketing site.
 */
export function marketingHref(path) {
  const p = path.startsWith('/') ? path : `/${path}`;
  if (IS_APP && MARKETING_URL) return `${MARKETING_URL}${p}`;
  return p;
}
